// Patient-facing landing page for a per-minute telehealth appointment that already
// exists in `pending_payment` (the booking form creates it exactly like any other slot —
// gateway-flow-c-design.md §5.1 — the ceiling becomes `payment_amount_cents`, so the
// existing hold/expiry machinery keeps working unchanged). This page is where the
// disclosure-then-consent step the Flow C money path REQUIRES actually happens: the
// quote (contract §2) is fetched, `ConsultCeilingDisclosure` states rate/floor/cap/the
// ceiling/no-show policy, and only once the consent checkbox is ticked does
// `POST /api/consults/{id}/intents` fire and the browser leave for Stripe Checkout.
//
// Route: `/consultation/confirmer?appointmentId=<id>&token=<manage_token>` (registered
// in routes/platformRoutes.jsx) — the `appointmentId`/`token` pair the booking response
// hands back for a per-minute slot.
import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { checkoutReturnFields } from "../lib/tenantPath";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertTriangle } from "lucide-react";
// Relative: gateway/ has no "@/" Jest shim yet — resolved for real, same convention as
// every other gateway-consuming component in this build.
import { useConsultQuote } from "../gateway/hooks/useConsultQuote";
import { useConsultIntent } from "../gateway/hooks/useConsultIntent";
import { gatewayErrorText } from "../gateway/errorText";
import { writePendingConsult } from "../lib/pendingConsult";
import ConsultCeilingDisclosure from "./ConsultCeilingDisclosure";
import MarketingOptIn from "../components/consent/MarketingOptIn";

// Contract §2: "OPTIONAL free text, max 1000 characters ... an over-long value is a 422
// rather than a silent truncation" — the server is the authority on the limit, this is
// only the visible guardrail that keeps a patient from typing past it in the first place.
const REASON_MAX_LENGTH = 1000;

/**
 * Refusals that are facts about the PRACTICE, not the consult, and outside the gateway's
 * closed code set (which would render them as "gw.error.UNKNOWN"). The practice's plan no
 * longer including video rooms (403 CAPABILITY_NOT_IN_PLAN), or video rooms not being
 * built (503 CAPABILITY_NOT_BUILT), is none of the patient's business: they get the
 * neutral sentence Reserver.jsx shows for the same facts.
 */
const PRACTICE_UNAVAILABLE_CODES = new Set(["CAPABILITY_NOT_IN_PLAN", "CAPABILITY_NOT_BUILT"]);
const refusalText = (code, t, tc) =>
  (PRACTICE_UNAVAILABLE_CODES.has(code) ? t("booking.practiceUnavailable") : gatewayErrorText(code, tc));

export default function ConsultBooking() {
  const t = useT("public");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [sp] = useSearchParams();
  const appointmentId = sp.get("appointmentId") || sp.get("apt") || "";
  const token = sp.get("token") || "";

  const { data: quote, loading, error } = useConsultQuote(appointmentId, token);
  const { creating, error: intentError, createIntent } = useConsultIntent(appointmentId);
  const [submitError, setSubmitError] = useState(null);
  const [reason, setReason] = useState("");
  // The OPTIONAL "send me news and offers" box — NOT the disclosure consent below (that
  // one authorises the money). Unticked until the patient ticks it (CASL); the server
  // records it only once the consultation is confirmed.
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const handleAccept = async () => {
    setSubmitError(null);
    // Stashed BEFORE the redirect fires — `/merci/consultation` has no other way to
    // recover this pair once Stripe sends the browser back (lib/pendingConsult.js).
    writePendingConsult({ appointmentId, token });
    const trimmedReason = reason.trim();
    const result = await createIntent({
      token,
      origin_url: window.location.origin,
      // Tenant-aware, like every other checkout in the product: in `/t/{slug}/` path
      // mode a bare "/merci/consultation" resolves to the PLATFORM apex, which serves the
      // SaaS marketing page (BUG-039), not this practice's site.
      ...checkoutReturnFields("/merci/consultation", "/consultation/confirmer"),
      locale: lang,
      // Optional — omitted entirely rather than sent as "" so an absent value on the
      // wire reads as "nothing to relay", not as an empty string someone typed and erased.
      ...(trimmedReason ? { reason_for_visit: trimmedReason } : {}),
      consent: { accepted: true, text_key: quote?.disclosureKeys?.[0] || "consult.disclosure.v1" },
      // Only a tick is sent: the server reads a literal `true` and nothing else.
      ...(marketingOptIn ? { marketing_opt_in: true } : {}),
    });
    if (!result.ok) setSubmitError(result.code);
  };

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 max-w-2xl mx-auto px-6" data-testid="consult-booking-page">
        <h1 className="font-display text-3xl text-eh-ink mb-6">{t("consult.booking.title")}</h1>

        {!appointmentId || !token ? (
          <p className="text-sm text-rose-600" role="alert" data-testid="consult-booking-missing-context">
            {t("consult.booking.missingContext")}
          </p>
        ) : loading && !quote ? (
          <div className="flex items-center gap-2 text-eh-muted py-8" data-testid="consult-booking-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> {t("consult.booking.loading")}
          </div>
        ) : error && !quote ? (
          <div className="py-8 text-center" data-testid="consult-booking-error">
            <AlertTriangle className="w-6 h-6 text-rose-500 mx-auto mb-2" />
            <p className="text-sm text-rose-600" role="alert">{refusalText(error.code, t, tc)}</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-eh-border-soft p-6 mb-4" data-testid="consult-booking-reason">
              <Label className="text-xs" htmlFor="consult-booking-reason-input">{t("consult.booking.reasonLabel")}</Label>
              <Textarea
                id="consult-booking-reason-input"
                value={reason}
                maxLength={REASON_MAX_LENGTH}
                placeholder={t("consult.booking.reasonPlaceholder")}
                onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX_LENGTH))}
                className="mt-1.5"
                rows={3}
                data-testid="consult-booking-reason-input"
              />
              <p className="text-xs text-eh-muted text-right mt-1" data-testid="consult-booking-reason-counter">
                {t("consult.booking.reasonCounter", { count: reason.length })}
              </p>
            </div>
            <div className="mb-4">
              <MarketingOptIn
                form="consult"
                checked={marketingOptIn}
                onChange={setMarketingOptIn}
                disabled={creating}
              />
            </div>
            <ConsultCeilingDisclosure quote={quote} onAccept={handleAccept} />
            {(submitError || intentError) && (
              <p className="text-sm text-rose-600 mt-3" role="alert" data-testid="consult-booking-submit-error">
                {refusalText(submitError || intentError?.code, t, tc)}
              </p>
            )}
            {creating && (
              <p className="text-sm text-eh-muted mt-3 flex items-center gap-1.5" data-testid="consult-booking-redirecting">
                <Loader2 className="w-4 h-4 animate-spin" /> {t("consult.booking.redirecting")}
              </p>
            )}
          </>
        )}
      </section>
    </ThemedPageLayout>
  );
}
